import { useEffect, useState } from "react";
import closeButton from '../../assets/images/close.png';
import {
    getTenderProcessList,
    createTenderProcess,
    updateTenderProcess
} from "../../services/AppService";
import { useNavigate } from "react-router-dom";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import AddStaffTenderProcessForm from "../../components/dashboard/Prceedings/TenderProcess/AddStaffTenderProcessForm";
import ViewTenderProcessDetailsModal from "../../components/dashboard/Prceedings/TenderProcess/ViewTenderProcessDetailsModal";
import { useTranslation } from "react-i18next";

const TenderProcess = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tenders, setTenders] = useState([]);
    const [editingTender, setEditingTender] = useState(null);

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedTender, setSelectedTender] = useState(null);

    // Pagination & Search State
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const [confirmModal, setConfirmModal] = useState({ open: false, formData: null });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const showSuccess = (msg) => setMessageModal({ open: true, type: "success", title: "Success", message: msg });
    const showError = (msg) => setMessageModal({ open: true, type: "error", title: "Error", message: msg });

    // Handle Search Debouncing
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        fetchTenders();
    }, [page, size, debouncedSearch]);

    const fetchTenders = async () => {
        try {
            setLoading(true);
            const response = await getTenderProcessList(debouncedSearch, page, size);
            if (response) {
                setTenders(response.content || []);
                setTotalPages(response.totalPages || 0);
                setTotalElements(response.totalElements || 0);
            }
        } catch (error) {
            showError("Failed to load tender processes");
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = (formData) => {
        setIsModalOpen(false);
        setConfirmModal({ open: true, formData: formData });
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            if (editingTender) {
                await updateTenderProcess(editingTender.id, formData);
                showSuccess("Tender Process updated successfully");
            } else {
                await createTenderProcess(formData);
                showSuccess("Tender Process added successfully");
            }
            fetchTenders();
            setConfirmModal({ open: false, formData: null });
            setEditingTender(null);
        } catch (error) {
            showError(error.response?.data?.message || "Internal Server Error");
        }
    };

    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">{t("pageTitles.tenderProcessList")}</h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
            </div>

            <div className="text-end mb-2">
                <button className="btnAdd" onClick={() => { setEditingTender(null); setIsModalOpen(true); }}>
                    {t("common.addNew")}
                </button>
            </div>

            {/* Entries and Search Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t("common.show")}:</label>
                    <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }} className="border rounded px-2 py-1 text-sm outline-none">
                        {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label className="text-sm">{t("common.entries")}</label>
                </div>
                <input type="text" placeholder={t("tables.tenderProcess.search")} value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="border rounded px-3 py-1 text-sm outline-none w-full sm:w-64" />
            </div>

            <div className="overflow-x-auto">
                <table className="w-full border text-sm ctable rounded-lg">
                    <thead>
                        <tr className="bg-[#a0adf1] text-white">
                            <th className="px-3 py-2 text-center border-b border-black">{t("tables.srNo")}</th>
                            <th className="px-3 py-2 border-b border-black">{t("tables.workName")}</th>
                            <th className="px-3 py-2 border-b border-black">{t("tables.tenderProcess.tenderReferenceNo")}</th>
                            <th className="px-3 py-2 border-b border-black">{t("tables.tenderProcess.tenderId")}</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t("tables.view")}</th>
                            <th className="px-3 py-2 text-center border-b border-black">{t("tables.edit")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="text-center py-4">{t("tables.tenderProcess.loadingTenderProcess")}</td></tr>
                        ) : tenders.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-4 text-gray-500">{t("tables.tenderProcess.noTenderProcess")}</td></tr>
                        ) : (
                            tenders.map((item, idx) => (
                                <tr key={item.id} className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}>
                                    <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                    <td className="px-3 py-2">{item.workTypeName}</td>
                                    <td className="px-3 py-2">{item.tenderReferenceNo}</td>
                                    <td className="px-3 py-2">{item.tenderId}</td>
                                    <td className="px-3 py-2 text-center">
                                        <button
                                            className="px-3 btnImgView py-1 rounded text-xs cursor-pointer"
                                            onClick={() => {
                                                setSelectedTender(item);
                                                setViewModalOpen(true);
                                            }}
                                        ></button>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <button className="px-3 py-1 btnImgEdit rounded text-xs cursor-pointer" onClick={() => { setEditingTender(item); setIsModalOpen(true); }}></button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-between items-center mt-4 text-sm">
                <p>{t("common.showing")} {page * size + 1} {t("common.to")} {page * size + tenders.length} {t("common.of")} {totalElements} {t("common.entries")}</p>
                <div className="flex gap-2">
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>{t("common.previous")}</button>
                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                             className={`px-3 py-1 border rounded ${page === i
                                ? "bg-gradient-to-b from-white to-[#dcdcdc] border border-[#979797]"
                                : ""
                                }`}
                            onClick={() => setPage(i)}
                        >
                            {i + 1}
                        </button>
                    ))}
                    
                    <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={page === totalPages - 1}>{t("common.next")}</button>
                </div>
            </div>

            <AddStaffTenderProcessForm
                onSubmit={handleFormSubmit}
                editData={editingTender}
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingTender(null); }}
            />

            <ConfirmActionModal
                open={confirmModal.open}
                title={editingTender ? "Confirm Update" : "Confirm Addition"}
                message={`Are you sure you want to ${editingTender ? "update" : "save"} this tender process details?`}
                onConfirm={handleConfirmSave}
                onClose={() => setConfirmModal({ open: false, formData: null })}
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />

            <ViewTenderProcessDetailsModal
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                tender={selectedTender}
            />
        </div>
    );
};

export default TenderProcess;